import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Users, TrendingDown, RefreshCw, LogOut, Trash2, Search, Download } from "lucide-react";
import { Logo } from "../components/Logo";
import { API_URL } from "../data/site";

const STATUS = ["Novo", "Em contato", "Convertido"];
const statusCls = {
  Novo: "bg-[#00E676]/15 text-[#00E676] border-[#00E676]/30",
  "Em contato": "bg-amber-400/15 text-amber-300 border-amber-400/30",
  Convertido: "bg-sky-400/15 text-sky-300 border-sky-400/30",
};

export default function AdminDashboard({ user, onLogout }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("Todos");

  const headers = { Authorization: `Bearer ${localStorage.getItem("igreen_admin_token")}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/leads`, { headers });
      setLeads(data);
    } catch (err) {
      if (err.response?.status === 401) {
        onLogout();
      } else {
        toast.error("Erro ao carregar leads.");
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (lead, status) => {
    try {
      const { data } = await axios.patch(`${API_URL}/leads/${lead.id}`, { status }, { headers });
      setLeads((ls) => ls.map((l) => (l.id === lead.id ? data : l)));
    } catch {
      toast.error("Não foi possível atualizar o status.");
    }
  };

  const remove = async (lead) => {
    if (!window.confirm(`Remover o lead "${lead.name}"?`)) return;
    try {
      await axios.delete(`${API_URL}/leads/${lead.id}`, { headers });
      setLeads((ls) => ls.filter((l) => l.id !== lead.id));
      toast.success("Lead removido.");
    } catch {
      toast.error("Não foi possível remover.");
    }
  };

  const visible = leads.filter((l) => {
    const okFilter = filter === "Todos" || l.status === filter;
    const q = search.toLowerCase();
    const okSearch = !q || [l.name, l.email, l.whatsapp, l.interest, l.uf].filter(Boolean).some((v) => v.toLowerCase().includes(q));
    return okFilter && okSearch;
  });

  const counts = STATUS.reduce((acc, s) => ({ ...acc, [s]: leads.filter((l) => l.status === s).length }), {});

  const exportCsv = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const header = ["Data", "Nome", "WhatsApp", "E-mail", "Interesse", "Conta de luz", "UF", "Origem", "Status"];
    const rows = visible.map((l) => [
      new Date(l.created_at).toLocaleString("pt-BR"),
      l.name, l.whatsapp, l.email, l.interest, l.bill_range, l.uf, l.source, l.status,
    ]);
    const csv = "﻿" + [header, ...rows].map((r) => r.map(esc).join(";")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `leads-igreen-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`${visible.length} lead(s) exportado(s).`);
  };

  return (
    <div className="min-h-screen bg-[#04120E]" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#04120E]/85 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-3">
            <a href="/" className="text-xs text-emerald-100/50 hover:text-[#00E676] transition-colors">Ver site</a>
            <button
              onClick={onLogout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-1.5 rounded-full border border-white/15 text-xs font-semibold text-emerald-100/70 px-4 py-2 hover:bg-white/5 transition-colors"
            >
              <LogOut size={13} /> Sair
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white">Leads</h1>
            <p className="mt-1 text-sm text-emerald-100/50">Olá, {user.name}. Aqui estão os contatos que chegaram pelo site.</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={exportCsv}
              data-testid="admin-export-csv-button"
              className="inline-flex items-center gap-2 rounded-full bg-[#00E676] hover:bg-[#00C853] text-[#04120E] text-sm font-bold px-5 py-2.5 transition-colors"
            >
              <Download size={14} /> Exportar CSV
            </button>
            <button
              onClick={load}
              data-testid="admin-refresh-button"
              className="inline-flex items-center gap-2 rounded-full border border-[#00E676]/30 text-[#00E676] text-sm font-semibold px-5 py-2.5 hover:bg-[#00E676]/10 transition-colors"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Atualizar
            </button>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: "Total de leads", value: leads.length, icon: Users },
            { label: "Novos", value: counts["Novo"] || 0, icon: TrendingDown },
            { label: "Em contato", value: counts["Em contato"] || 0, icon: Users },
            { label: "Convertidos", value: counts["Convertido"] || 0, icon: TrendingDown },
          ].map((c, i) => (
            <div key={c.label} data-testid={`admin-stat-${i}`} className="rounded-2xl border border-white/10 bg-[#0B261E]/60 p-5">
              <c.icon size={17} className="text-[#00E676] mb-3" />
              <p className="font-heading text-2xl font-extrabold text-white">{c.value}</p>
              <p className="text-xs text-emerald-100/50 mt-1">{c.label}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-emerald-100/40" />
            <input
              data-testid="admin-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nome, WhatsApp, interesse…"
              className="w-full rounded-full border border-white/12 bg-[#0B261E]/70 pl-10 pr-4 py-2.5 text-sm text-white placeholder:text-emerald-100/35 outline-none focus:border-[#00E676]/50 transition-colors"
            />
          </div>
          <div className="flex gap-2">
            {["Todos", ...STATUS].map((s) => (
              <button
                key={s}
                data-testid={`admin-filter-${s.toLowerCase().replace(" ", "-")}`}
                onClick={() => setFilter(s)}
                className={`rounded-full text-xs font-semibold px-4 py-2.5 border transition-colors duration-300 ${
                  filter === s ? "bg-[#00E676] text-[#04120E] border-[#00E676]" : "border-white/15 text-emerald-100/60 hover:bg-white/5"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6 rounded-2xl border border-white/10 bg-[#0B261E]/60 overflow-hidden" data-testid="admin-leads-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-emerald-100/40 border-b border-white/10">
                  <th className="px-5 py-4 font-semibold">Data</th>
                  <th className="px-5 py-4 font-semibold">Nome</th>
                  <th className="px-5 py-4 font-semibold">Contato</th>
                  <th className="px-5 py-4 font-semibold">Interesse</th>
                  <th className="px-5 py-4 font-semibold">Conta / UF</th>
                  <th className="px-5 py-4 font-semibold">Origem</th>
                  <th className="px-5 py-4 font-semibold">Status</th>
                  <th className="px-5 py-4" />
                </tr>
              </thead>
              <tbody>
                {visible.map((l) => (
                  <tr key={l.id} data-testid={`admin-lead-row-${l.id}`} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors">
                    <td className="px-5 py-4 text-emerald-100/50 whitespace-nowrap text-xs">
                      {new Date(l.created_at).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}
                    </td>
                    <td className="px-5 py-4 font-semibold text-white">{l.name}</td>
                    <td className="px-5 py-4 text-emerald-100/60 text-xs">
                      {l.whatsapp && <p>{l.whatsapp}</p>}
                      {l.email && <p className="text-emerald-100/40">{l.email}</p>}
                      {!l.whatsapp && !l.email && "—"}
                    </td>
                    <td className="px-5 py-4 text-emerald-100/60 text-xs">{l.interest || "—"}</td>
                    <td className="px-5 py-4 text-emerald-100/60 text-xs whitespace-nowrap">
                      {l.bill_range || "—"}{l.uf ? ` · ${l.uf}` : ""}
                    </td>
                    <td className="px-5 py-4 text-emerald-100/40 text-xs">{l.source}</td>
                    <td className="px-5 py-4">
                      <select
                        value={l.status}
                        onChange={(e) => setStatus(l, e.target.value)}
                        data-testid={`admin-lead-status-${l.id}`}
                        className={`rounded-full border text-[11px] font-bold px-3 py-1.5 bg-transparent outline-none cursor-pointer ${statusCls[l.status]}`}
                      >
                        {STATUS.map((s) => <option key={s} value={s} className="bg-[#04120E] text-white">{s}</option>)}
                      </select>
                    </td>
                    <td className="px-5 py-4">
                      <button
                        onClick={() => remove(l)}
                        data-testid={`admin-lead-delete-${l.id}`}
                        aria-label="Remover lead"
                        className="p-2 rounded-lg text-emerald-100/40 hover:text-red-400 hover:bg-red-400/10 transition-colors"
                      >
                        <Trash2 size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
                {!loading && visible.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-5 py-14 text-center text-sm text-emerald-100/40" data-testid="admin-leads-empty">
                      Nenhum lead por aqui ainda. Compartilhe o site e eles aparecem nesta tela.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
